import React from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import {
  CommunityEventStore,
  CommunityPageCardStore,
  CommunityPageConfigStore,
  ConversationCallStore,
  MembershipTypeStore,
} from '@seedlang/stores';
import { AppUI } from '@seedlang/state';
import CommunityPage from 'components/community_page/community_page';
import PageTitle from 'components/common/page_title';
import Spinner from 'components/spinner';
import ViewAsSelect from 'components/view_as_select';

const SIGNED_OUT = 'signed_out';
const SIGNED_IN_WITHOUT_MEMBERSHIP = 'signed_in_without_membership';

@observer
class Community extends React.Component {
  constructor(props) {
    super(props);
    this.state = { viewAs: null, joining: false };
    CommunityPageConfigStore.clearShowData();
    CommunityEventStore.clearIndexData();
    CommunityPageCardStore.clearIndexData();
    ConversationCallStore.clearIndexData();
    if (AppUI.user?.anyAdmin) {
      MembershipTypeStore.getIndex();
    }
    CommunityPageConfigStore.getShow();
    CommunityEventStore.getIndex({limit: 1000});
    CommunityPageCardStore.getIndex({limit: 1000});
    ConversationCallStore.getIndex({limit: 1000});
  }

  componentWillUnmount() {
    this.abortAudienceRequests();
    CommunityPageConfigStore.clearShowData();
    CommunityEventStore.clearIndexData();
    CommunityPageCardStore.clearIndexData();
    ConversationCallStore.clearIndexData();
  }

  abortRequest = (requestField) => {
    if (this[requestField]?.abort) {
      this[requestField].abort();
    }
    this[requestField] = null;
  };

  abortAudienceRequests = () => {
    this.abortRequest('communityEventRequest');
    this.abortRequest('communityPageCardRequest');
    this.abortRequest('conversationCallRequest');
  };

  reloadStore = (store, requestField, queryStrings) => {
    this.abortRequest(requestField);
    let request;
    const callback = (response) => {
      if (this[requestField] === request) {
        this[requestField] = null;
        store.afterGetIndex(response);
      }
    };
    request = queryStrings
      ? store.reloadIndexFiltered({queryStrings, callback})
      : store.reloadIndex(callback);
    this[requestField] = request;
  };

  @computed get loading() {
    return (
      CommunityPageConfigStore.hasOutstandingRequests ||
      CommunityEventStore.hasOutstandingRequests ||
      CommunityPageCardStore.hasOutstandingRequests ||
      (AppUI.user?.anyAdmin && MembershipTypeStore.hasOutstandingRequests) ||
      (
        ConversationCallStore.hasOutstandingRequests &&
        !ConversationCallStore.initialRequestReceived
      )
    );
  }

  refreshConversationCalls = () => {
    const queryStrings = this.state.viewAs
      ? {view_as: this.state.viewAs}
      : null;
    this.reloadStore(ConversationCallStore, 'conversationCallRequest', queryStrings);
  };

  @computed get viewAsOptions() {
    return [
      { value: SIGNED_OUT, label: 'Signed Out' },
      { value: SIGNED_IN_WITHOUT_MEMBERSHIP, label: 'Signed In without Membership' },
      ...MembershipTypeStore.indexData.map(membershipType => ({
        value: membershipType.id,
        label: membershipType.name,
      })),
    ];
  }

  @computed get selectedViewAs() {
    return this.state.viewAs ||
      AppUI.user.currentUserGroup?.membershipTypeId ||
      SIGNED_IN_WITHOUT_MEMBERSHIP;
  }

  handleViewAsChange = (viewAs) => {
    if (!viewAs) { return; }

    this.setState({viewAs}, () => {
      const queryStrings = {view_as: viewAs};
      this.reloadStore(CommunityEventStore, 'communityEventRequest', queryStrings);
      this.reloadStore(CommunityPageCardStore, 'communityPageCardRequest', queryStrings);
      this.reloadStore(ConversationCallStore, 'conversationCallRequest', queryStrings);
    });
  };

  handleJoinConversationCall = (call) => {
    if (!call?.id || this.state.viewAs || this.state.joining) {
      return;
    }

    this.setState({joining: true});
    const openedWindow = window.open('about:blank', '_blank');
    const finishJoining = () => this.setState({joining: false});
    const closeOpenedWindow = () => {
      if (openedWindow && !openedWindow.closed) {
        openedWindow.close();
      }
    };

    ConversationCallStore.join(
      {
        ids: {conversationCallId: call.id},
        failureCallback: () => {
          closeOpenedWindow();
          finishJoining();
        },
        onRequestError: () => {
          closeOpenedWindow();
          finishJoining();
        },
      },
      response => {
        if (!response?.joinUrl) {
          closeOpenedWindow();
          finishJoining();
          return;
        }

        finishJoining();
        if (openedWindow && !openedWindow.closed) {
          openedWindow.location = response.joinUrl;
        } else {
          window.location.assign(response.joinUrl);
        }
      },
    );
  };

  @computed get conversationCalls() {
    return ConversationCallStore.indexData
      .slice()
      .sort((first, second) => first.time?.date - second.time?.date)
      .map(conversationCall => ({
        id: conversationCall.id,
        title: conversationCall.title,
        date: conversationCall.time?.date,
        accessStatus: conversationCall.accessStatus,
        getReady: conversationCall.getReady,
      }));
  }

  @computed get cards() {
    return CommunityPageCardStore.indexData.map(card => ({
      title: card.title,
      description: card.description,
      primary_button_label: card.primaryButtonLabel,
      primary_button_url: card.primaryButtonUrl,
      secondary_button_label: card.secondaryButtonLabel,
      secondary_button_url: card.secondaryButtonUrl,
      image_url: card.imageUrl,
    }));
  }

  @computed get communityEvents() {
    return CommunityEventStore.indexData.map(communityEvent => ({
      title: communityEvent.title,
      subtitle: communityEvent.description,
      button_label: communityEvent.membersButtonLabel || communityEvent.nonMembersButtonLabel,
      button_url: communityEvent.membersButtonUrl || communityEvent.nonMembersButtonUrl,
    }));
  }

  render() {
    if (this.loading) {
      return <Spinner />;
    }

    const config = CommunityPageConfigStore.showData;

    return (
      <>
        <PageTitle>{config.title}</PageTitle>
        {
          AppUI.user?.anyAdmin && MembershipTypeStore.initialRequestReceived &&
            <ViewAsSelect
              options={this.viewAsOptions}
              value={this.selectedViewAs}
              onChange={this.handleViewAsChange}
            />
        }
        <CommunityPage
          accentColor={AppUI.site?.accentColor}
          title={config.title}
          description={config.subtitle}
          conversationCalls={this.conversationCalls}
          cards={this.cards}
          eventsCardImageUrl={config.eventsCardImage?.image?.url}
          eventsCardTitle={config.eventsCardTitle}
          eventsCardDescription={config.eventsCardDescription}
          communityEvents={this.communityEvents}
          joinBlocked={Boolean(this.state.viewAs)}
          joining={this.state.joining}
          onJoinConversationCall={this.handleJoinConversationCall}
          onConversationCallsRefresh={this.refreshConversationCalls}
        />
      </>
    );
  }
}

export default Community;
